import React, { useEffect } from 'react';
import { useState } from 'react';

// Function to generate consistent color from stop ID
const getStopColor = (stopId) => {
  if (!stopId) return '#6B7280'; // Default gray
  
  // Create a hash from the stopId
  let hash = 0;
  const idString = String(stopId);
  for (let i = 0; i < idString.length; i++) {
    hash = idString.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate HSL color with good saturation and lightness for visibility
  const hue = Math.abs(hash) % 360;
  const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
  const lightness = 45 + (Math.abs(hash) % 15); // 45-60%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

function FixedItinerary({ 
  isPhone, 
  schedule, // Socket-updated itinerary data (like jobs in LoopedItinerary)
  onStopSelect, 
  onStopAction,
  onStopActionsSelect, // New prop for row-click activation
  currentStopIndex = 0,
  completedStops = [],
  scheduleStatus = 'active',
  scheduleStartTime = null,
  scheduleEndTime = null,
  fixedRouteData = null, // Keep for backward compatibility but not used
  currentRunSchedule = null,
  breakLogs = [], // Server-authoritative break logs
  endPointLog = null // Server-authoritative endpoint log
}) {
 
  const [steps, setSteps] = useState([]);
  
  // NOTE: This component now works like LoopedItinerary - uses socket-updated schedule prop
  // and breakLogs/endPointLog props instead of fixedRouteData
  
  // Helper function to check if a stop has a scheduled break
  const hasScheduledBreak = (stopId, scheduleId, itineraryId) => {

    const result = breakLogs.some(breakLog =>{
      return breakLog.StopId === stopId &&  breakLog.ScheduleId === scheduleId ? true : false;
    });

    return result;
  };

  // Helper function to check if a stop is the end point
  const isEndPoint = (stopId) => {
    if (!endPointLog) return false;
    const result = endPointLog.StopId === stopId;
    return result;
  }; 

 const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      if (typeof timeString === 'string') {
        if (timeString.includes(':')) {
          const today = new Date();
          const timeParts =  timeString.split("T")[1].split(':');
          let hours = parseInt(timeParts[0]);
          const minutes = parseInt(timeParts[1]);
          
          
          today.setHours(hours, minutes, 0, 0);
          return today.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'});
        }
      }
      return timeString;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

useEffect(() => {
    // Use schedule as primary data source (like LoopedItinerary uses jobs/itinerary)
    // The schedule prop is updated with socket data from the parent component
    
    if (schedule && Array.isArray(schedule)) {
      // For fixed routes, we show upcoming stops (not completed)
      // Filter out completed stops using the completedTime property (server-authoritative)
      let filteredSteps = schedule.filter((step) => {
        // Include stops that haven't been completed (no completedTime)
        return !step.completedTime;
      });
      
      setSteps(filteredSteps);
    } else {
      setSteps([]);
    }
  }, [schedule, breakLogs, endPointLog]);

let firstBreak=true;
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        
        
        {/* Status Indicator */}
        {scheduleStatus !== 'active' && (
          <div className={`px-3 py-1 rounded-full text-sm font-medium mb-2 inline-block ${
            scheduleStatus === 'pre-schedule' ? 'bg-yellow-100 text-yellow-800' :
            scheduleStatus === 'post-schedule' ? 'bg-green-100 text-green-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {scheduleStatus === 'pre-schedule' ? '⏰ On Break' :
             scheduleStatus === 'post-schedule' ? '✅ Schedule Complete' :
             '🚌 Active'}
          </div>
        )}
        

      </div>

      {/* Schedule Stops */}
      <div className="flex-1 overflow-y-auto">
        {!!steps && steps.length > 0 ? (
          <div className="space-y-3">
            {steps.slice(0, 10).map((stop, index) => {
              // Extract stop properties with fallbacks for different naming conventions (like LoopedItinerary)
              const stopId = stop.id;
              const scheduleId = stop.scheduleId || (currentRunSchedule ? currentRunSchedule.Id : null);
              const stopName = stop.StopName || stop.LandmarkName || stop.Name || stop.address || 'Unknown Stop';
              const stopColor = getStopColor(stopId);
              
              // Use combined logic like LoopedItinerary (both server data AND breakLogs prop)
              const hasBreak = stop.isBreak || hasScheduledBreak(stopId, scheduleId, stop.itineraryId);
              const isEndStop = stop.endStep;
              const isCurrent = stop.currentWaypoint;


              
              return (
                <div
                  key={`stop_${stopId}_${index}`}
                  className={`
                    border rounded-lg px-3 py-1 cursor-pointer transition-all duration-200
                    border-gray-200 bg-white hover:bg-gray-50
                  `}
                  onClick={() => {
                    // Use row-click activation like LoopedItinerary
                    if (onStopActionsSelect) {
                      const stopData = {
                        stopId: stopId,
                        Id: stopId,
                        StopId: stop.StopId || stopId,
                        ItineraryId: stop.ItineraryId || stopId,
                        Name: stopName,
                        address: stopName,
                        StopName: stopName,
                        LandmarkName: stopName,
                        estimatedArrivalTime: stop.eta || stop.displayTime,
                        arrival: stop.eta || stop.displayTime,
                        displayTime: stop.eta || stop.displayTime,
                        eta: stop.eta,
                        currentWaypoint: isCurrent,
                        completed: stop.completedTime,
                        completedTime: stop.completedTime,
                        // Server-provided state properties (use combined logic)
                        breakStep: hasBreak,
                        beginStep: stop.beginStep,
                        endStep: isEndStop,
                        breakTime: stop.breakTime,
                        breakDepartureTime: stop.breakDepartureTime,
                        // Include server IDs
                        RunId: stop.RunId,
                        ScheduleId: stop.ScheduleId,
                        Latitude: stop.Latitude,
                        Longitude: stop.Longitude,
                        SortOrder: stop.SortOrder,
                        ETAOffsetMins: stop.ETAOffsetMins,
                        ...stop // Include all other properties
                      };
                      onStopActionsSelect(stopData);
                    } else {
                      // Fallback to original behavior
                      onStopSelect && onStopSelect(index, stop);
                    }
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-3">
                      {/* Stop Indicator */}
                      <div className="flex flex-col items-center">
                        <span 
                          className={`
                            w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold
                           
                          `}
                          style={{
                            backgroundColor: stopColor,
                            color: 'white'
                        }}>
                       
                        </span>
                     
                        
                      </div>

                      {/* Stop Details */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <div className={`font-semibold text-2xl`}>
                            {stopName}
                          </div>
                          
                          {/* Visual indicators for breaks and end points (using combined logic like LoopedItinerary) */}
                          <div className="flex items-center space-x-1">
                            {hasBreak && firstBreak && (
                              <span 
                                className="inline-flex items-center justify-center w-5 h-5 text-xs bg-yellow-100 text-yellow-600 rounded-full border border-yellow-200"
                                title={`Break stop${stop.breakTime ? ` (${stop.breakTime} min)` : ''}${stop.breakDepartureTime ? ` - departs ${formatTime(stop.breakDepartureTime)}` : ''}`}
                              >
                                ☕
                              </span>
                            )}
                            {isEndStop && (
                              <span 
                                className="inline-flex items-center justify-center w-5 h-5 text-xs bg-purple-100 text-purple-600 rounded-full border border-purple-200"
                                title="End point"
                              >
                                🏁
                              </span>
                            )}
                            {stop.beginStep && (
                              <span 
                                className="inline-flex items-center justify-center w-5 h-5 text-xs bg-green-100 text-green-600 rounded-full border border-green-200"
                                title="Start point"
                              >
                                🚀
                              </span>
                            )}
                            
                            {/* Add debugging info for current waypoint */}
                            {isCurrent && (
                              <span 
                                className="inline-flex items-center justify-center w-5 h-5 text-xs bg-blue-100 text-blue-600 rounded-full border border-blue-200"
                                title="Current waypoint"
                              >
                                📍
                              </span>
                            )}


                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Time and Status */}
                    <div className="text-right">
                      <div className={`text-lg font-medium`}>
                        {formatTime(stop.eta)}
                      </div>
                    </div>
                  </div>



                 
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-gray-500 text-center py-6">
            No upcoming stops
          </div>
        )}
      </div>
    </div>
  );
}

export default FixedItinerary;

// Export the color function for use in other components
export { getStopColor };
